/**
 * Shared input parsers — used by the dashboard cascade builder
 * (`TaskEricInput`) and re-exported into the worker for any future
 * server-side validation. Pure ESM, no DOM / Node deps so the same
 * file runs in both runtimes.
 *
 * The cascade and the SMS brain both accept the same shorthand for
 * "Year Make Model VIN" and "Name, phone, email" pasted by mechanics
 * on the shop floor. Drift between the two has bitten us once already
 * (see Bug "Re-align TaskEric cascade with Eric's brain" 2026-05-23);
 * keep all regex + tokenization here so they stay in lockstep.
 */

// VINs are 17 characters of [A-HJ-NPR-Z0-9] — real VINs exclude I, O,
// and Q because they look like 1, 0, and 0. Anchored versions are for
// "is this whole string a VIN?"; non-anchored for "is there a VIN
// somewhere in this string?".
export const VIN_REGEX = /^[A-HJ-NPR-Z0-9]{17}$/i
export const VIN_INLINE_REGEX = /\b[A-HJ-NPR-Z0-9]{17}\b/i

// North-American phone — leading + optional, then a digit, then 6+
// chars of digits / spaces / dashes / dots / parens. Permissive on
// purpose; mechanics paste in every format imaginable. Normalize via
// `normalizePhone` after matching.
export const PHONE_INLINE_REGEX = /\+?\d[\d\s\-().]{6,}/

// Email — same shape used everywhere; tolerates + tags and most TLDs.
export const EMAIL_INLINE_REGEX = /[\w.+-]+@[\w.-]+\.\w+/

// Four-digit year in 1900–2099. Used by `parseVehicleInput` to extract
// the model year from a free-form vehicle string.
export const YEAR_INLINE_REGEX = /\b(19|20)\d{2}\b/

// The inline problem-report prefix. A composer message that starts (after
// trimming leading whitespace, case-insensitive) with `report:` is NOT sent to
// Eric — it's diverted to the eric_problem_reports table linked to the preceding
// turn (worker/lib/reports.ts). MIRRORED in worker/lib/reports.ts (the worker /
// src TS projects are isolated by `tsc -b`, so the file can't be literally
// shared) — KEEP THE TWO IN SYNC.
const REPORT_PREFIX_REGEX = /^\s*report:\s*/i

export function parseReportPrefix(input: string): { isReport: boolean; text: string } {
  const match = input.match(REPORT_PREFIX_REGEX)
  if (!match) return { isReport: false, text: '' }
  return { isReport: true, text: input.slice(match[0].length).trim() }
}

export function isValidVin(input: string): boolean {
  return VIN_REGEX.test(input.trim())
}

export function extractVin(input: string): string | null {
  const match = input.toUpperCase().match(VIN_INLINE_REGEX)
  return match ? match[0] : null
}

export function extractEmail(input: string): string | null {
  const match = input.match(EMAIL_INLINE_REGEX)
  return match ? match[0] : null
}

export function extractPhone(input: string): string | null {
  const match = input.match(PHONE_INLINE_REGEX)
  if (!match) return null
  return normalizePhone(match[0])
}

// Keep digits and a leading +. Doesn't enforce country code shape —
// the receiving form does that on save.
export function normalizePhone(raw: string): string {
  return raw.replace(/[^\d+]/g, '')
}

/**
 * Parse a free-form vehicle string like `2019 Subaru Forester
 * JF2SKAEC5KH123456` into structured fields. VIN wins if present; year
 * is the first 4-digit number in a plausible range; whatever's left
 * tokenises into make (first token) + model (rest).
 */
export function parseVehicleInput(input: string): {
  year: number | null
  make: string | null
  model: string | null
  vin: string | null
} {
  const vin = extractVin(input)

  const yearMatch = input.match(YEAR_INLINE_REGEX)
  const year = yearMatch ? Number(yearMatch[0]) : null

  let rest = input
  if (vin) rest = rest.replace(new RegExp(vin, 'i'), '')
  if (yearMatch) rest = rest.replace(yearMatch[0], '')
  rest = rest.replace(/[,;]+/g, ' ').replace(/\s+/g, ' ').trim()

  const tokens = rest.length > 0 ? rest.split(/\s+/) : []
  const make = tokens.length >= 1 ? tokens[0] : null
  const model = tokens.length >= 2 ? tokens.slice(1).join(' ') : null

  return { year, make, model, vin }
}

/**
 * Parse a "Name, phone, email" shorthand. Email + phone are pulled out
 * by regex; whatever remains after stripping them and trimming
 * separators is the name.
 */
export function parseCustomerInput(input: string): {
  name: string
  phone: string | null
  email: string | null
} {
  const email = extractEmail(input)
  const phoneMatch = input.match(PHONE_INLINE_REGEX)
  const phone = phoneMatch ? normalizePhone(phoneMatch[0]) : null

  let name = input
  if (email) name = name.replace(email, '')
  if (phoneMatch) name = name.replace(phoneMatch[0], '')
  name = name.replace(/[,;]+/g, ' ').replace(/\s+/g, ' ').trim()

  return { name, phone, email }
}
